import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { extrairFaturaCrm } from '../../../src/services/crmExtracaoApi';
import { mapExtracaoFaturaCrmToFaturaData } from './faturaExtraction';

interface EnviarFaturaModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Chamado com os dados extraídos (titular, endereço, UC) e o PDF original, assim que o processamento termina com sucesso. */
    onExtracted: (data: Record<string, any>, file: File) => void;
}

const EnviarFaturaModal: React.FC<EnviarFaturaModalProps> = ({ isOpen, onClose, onExtracted }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const backdropMouseDownRef = useRef(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type === 'application/pdf') {
                setSelectedFile(file);
                setUploadError(null);
            } else {
                setUploadError('Por favor, selecione um arquivo PDF válido.');
                setSelectedFile(null);
            }
        }
    };

    const resetAndClose = () => {
        setSelectedFile(null);
        setUploadError(null);
        setUploadSuccess(false);
        onClose();
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Selecione um arquivo PDF para importar.');
            return;
        }
        const fileToSend = selectedFile;

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        const resposta = await extrairFaturaCrm(fileToSend);

        if (!resposta.ok) {
            console.error('[Enviar Fatura] Extração falhou:', resposta.error);
            setUploadError(resposta.error || 'Não foi possível processar a fatura enviada.');
            setIsUploading(false);
            return;
        }

        // Regra de ouro do parser: nunca emitir número errado em silêncio — faturas que não
        // reconciliam viram 'falhou' e não devem preencher o formulário com dado incerto.
        if (resposta.reconciliacao?.status === 'falhou') {
            const criticos = resposta.reconciliacao.checagens
                .filter((c) => c.tipo === 'critico')
                .map((c) => c.mensagem)
                .slice(0, 3)
                .join(' | ');
            console.error('[Enviar Fatura] Reconciliação falhou:', resposta.reconciliacao);
            setUploadError(`A fatura não pôde ser lida com confiança suficiente${criticos ? `: ${criticos}` : '.'}`);
            setIsUploading(false);
            return;
        }

        if (!resposta.estruturado) {
            console.error('[Enviar Fatura] Resposta de sucesso sem dados extraídos:', resposta);
            setUploadError('A fatura foi processada, mas nenhum dado foi retornado. Preencha os dados manualmente.');
            setIsUploading(false);
            return;
        }

        const data = mapExtracaoFaturaCrmToFaturaData(resposta);

        setIsUploading(false);
        setUploadSuccess(true);
        setTimeout(() => {
            setSelectedFile(null);
            setUploadSuccess(false);
            onExtracted(data, fileToSend);
        }, 1500);
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) resetAndClose(); backdropMouseDownRef.current = false; }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <FileText className="text-primary" />
                        Importar Fatura (PDF)
                    </h2>
                    {!isUploading && (
                        <button onClick={resetAndClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <X size={24} />
                        </button>
                    )}
                </div>

                <div className="p-6">
                    {isUploading ? (
                        <div className="flex flex-col items-center justify-center py-8">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="120" height="120">
                                <defs>
                                    <style>
                                        {`
                                            .spin-forward {
                                                transform-origin: 50px 50px;
                                                animation: rotate-forward 1.5s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
                                            }
                                            .spin-reverse {
                                                transform-origin: 50px 50px;
                                                animation: rotate-reverse 2s linear infinite;
                                            }
                                            .pulse-energy {
                                                transform-origin: 50px 50px;
                                                animation: pulse 1.5s ease-in-out infinite alternate;
                                            }

                                            @keyframes rotate-forward {
                                                0% { transform: rotate(0deg); }
                                                100% { transform: rotate(360deg); }
                                            }
                                            @keyframes rotate-reverse {
                                                0% { transform: rotate(360deg); }
                                                100% { transform: rotate(0deg); }
                                            }
                                            @keyframes pulse {
                                                0% { transform: scale(0.85); opacity: 0.8; }
                                                100% { transform: scale(1.05); opacity: 1; }
                                            }
                                        `}
                                    </style>
                                </defs>
                                <circle className="spin-forward" cx="50" cy="50" r="40" fill="none" stroke="#3A5A40" strokeWidth="5" strokeDasharray="160 80" strokeLinecap="round" />
                                <circle className="spin-reverse" cx="50" cy="50" r="28" fill="none" stroke="#E09F3E" strokeWidth="4" strokeDasharray="60 100" strokeLinecap="round" />
                                <path className="pulse-energy" d="M51 32 L39 53 L49 53 L47 68 L61 46 L51 46 Z" fill="#3A5A40" />
                            </svg>
                            <p className="mt-6 text-lg font-medium text-gray-700 dark:text-gray-200">Enviando fatura...</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2 max-w-[80%]">
                                Por favor, aguarde enquanto o arquivo é processado.
                            </p>
                        </div>
                    ) : uploadSuccess ? (
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 text-green-600 dark:text-green-400">
                                <CheckCircle2 size={40} />
                            </div>
                            <p className="text-lg font-bold text-gray-800 dark:text-gray-100">Dados extraídos com sucesso!</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Abrindo formulário com os dados preenchidos...</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative cursor-pointer">
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-full text-primary dark:text-primary-light mb-4">
                                    <Upload size={32} />
                                </div>
                                {selectedFile ? (
                                    <div className="text-center">
                                        <p className="font-medium text-gray-800 dark:text-gray-200">{selectedFile.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <p className="font-medium text-gray-700 dark:text-gray-300">Clique para selecionar um arquivo</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Apenas arquivos PDF</p>
                                    </div>
                                )}
                            </div>

                            {uploadError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm border border-red-100 dark:border-red-800">
                                    <AlertCircle size={16} className="shrink-0" />
                                    <p>{uploadError}</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <button
                                    onClick={resetAndClose}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={!selectedFile}
                                    className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Upload size={18} />
                                    Enviar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EnviarFaturaModal;
